import React, { useEffect, useRef, useState } from "react";
import { X, Send, Bot, User, Sparkles } from "lucide-react";
import api from "../lib/api";

/**
 * Simple scripted chat flow:
 *   1. greeting -> ask what they need
 *   2. ask course interest
 *   3. ask name
 *   4. ask phone
 *   5. save lead + close
 */
const COURSES = [
  "Mechatronics",
  "Precision Machining",
  "Tool & Die Design",
  "CNC Programming",
  "Industrial Automation",
  "Welding",
  "Mold Design",
  "Other",
];

const phases = {
  GREETING: "greeting",
  ASK_COURSE: "ask_course",
  ASK_NAME: "ask_name",
  ASK_PHONE: "ask_phone",
  DONE: "done",
};

function validPhone(p) {
  const digits = p.replace(/\D/g, "");
  return digits.length >= 8 && digits.length <= 15;
}

export default function ChatAssistant() {
  const [open, setOpen] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const [unread, setUnread] = useState(0);
  const [messages, setMessages] = useState([]);
  const [phase, setPhase] = useState(phases.GREETING);
  const [draft, setDraft] = useState("");
  const [lead, setLead] = useState({ name: "", phone: "", course_interest: "", initial_message: "" });
  const endRef = useRef(null);

  // Auto-open after 15s on first visit
  useEffect(() => {
    const t = setTimeout(() => {
      if (hasShown) return;
      setHasShown(true);
      bootSession();
      setOpen(true);
    }, 15000);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const bootSession = () => {
    if (messages.length) return;
    pushBot("\ud83d\udc4b Hi! Welcome to Kiki Centre for Technology. How may I help you today?");
    setUnread((u) => u + 1);
  };

  const pushBot = (text) => setMessages((m) => [...m, { from: "bot", text, ts: Date.now() }]);
  const pushUser = (text) => setMessages((m) => [...m, { from: "user", text, ts: Date.now() }]);

  const handleSend = async (val) => {
    const text = (val ?? draft).trim();
    if (!text) return;
    pushUser(text);
    setDraft("");

    await new Promise((r) => setTimeout(r, 400)); // typing delay

    if (phase === phases.GREETING) {
      setLead((l) => ({ ...l, initial_message: text }));
      pushBot("Great! Which course are you interested in? You can choose from the list below or type your own.");
      setPhase(phases.ASK_COURSE);
    } else if (phase === phases.ASK_COURSE) {
      setLead((l) => ({ ...l, course_interest: text }));
      pushBot("Awesome choice! Could you please share your name so our team can reach out?");
      setPhase(phases.ASK_NAME);
    } else if (phase === phases.ASK_NAME) {
      setLead((l) => ({ ...l, name: text }));
      pushBot(`Thanks, ${text}! What is the best phone number to contact you on?`);
      setPhase(phases.ASK_PHONE);
    } else if (phase === phases.ASK_PHONE) {
      if (!validPhone(text)) {
        pushBot("That doesn't look like a valid phone number. Please enter a phone number with country code (e.g. +91 98xx xxxx xx).");
        return;
      }
      const updated = { ...lead, phone: text };
      setLead(updated);
      try {
        await api.post("/chat-leads", {
          name: updated.name,
          phone: updated.phone,
          course_interest: updated.course_interest,
          initial_message: updated.initial_message,
          transcript: [...messages, { from: "user", text, ts: Date.now() }],
        });
        pushBot(`Perfect! \ud83c\udf89 We've received your details. Our admissions team will call you shortly. In the meantime, feel free to browse our courses.`);
      } catch (e) {
        pushBot("Hmm, something went wrong saving your details. Please call us at +91 8800288994 or try the contact form.");
      }
      setPhase(phases.DONE);
    } else {
      pushBot("Thanks for chatting! For more, please reach out via our contact page or WhatsApp.");
    }

    if (!open) setUnread((u) => u + 1);
  };

  const onOpen = () => {
    setOpen(true);
    setUnread(0);
    if (!messages.length) bootSession();
  };

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button
          onClick={onOpen}
          aria-label="Open chat assistant"
          className="fixed bottom-6 left-6 z-[70] group flex items-center gap-3 pl-3 pr-5 py-3 rounded-full bg-[#0B2A6B] hover:bg-[#061a47] text-white shadow-2xl transition hover:scale-105"
        >
          <div className="relative h-10 w-10 rounded-full bg-yellow-400 flex items-center justify-center">
            <Bot className="h-5 w-5 text-[#0B2A6B]" />
            {unread > 0 && (
              <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center animate-pulse">
                {unread}
              </span>
            )}
          </div>
          <div className="text-left leading-tight">
            <div className="text-xs text-yellow-400 font-bold flex items-center gap-1">
              <Sparkles className="h-3 w-3" /> AI Assistant
            </div>
            <div className="text-sm font-semibold">How may I help?</div>
          </div>
        </button>
      )}

      {/* Panel */}
      <div
        className={`fixed bottom-6 left-6 z-[70] w-[360px] max-w-[calc(100vw-3rem)] rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden flex flex-col transition-all duration-300 origin-bottom-left ${
          open ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-90 pointer-events-none"
        }`}
        style={{ height: open ? 520 : 0 }}
      >
        {/* Header */}
        <div className="bg-[#0B2A6B] text-white p-4 flex items-center gap-3">
          <div className="relative h-10 w-10 rounded-full bg-yellow-400 flex items-center justify-center">
            <Bot className="h-5 w-5 text-[#0B2A6B]" />
            <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-green-400 border-2 border-[#0B2A6B]" />
          </div>
          <div className="flex-1">
            <div className="font-bold text-sm flex items-center gap-1">
              Kiki Assistant <Sparkles className="h-3 w-3 text-yellow-400" />
            </div>
            <div className="text-[11px] text-yellow-200">Online • Typically replies instantly</div>
          </div>
          <button onClick={() => setOpen(false)} className="h-8 w-8 rounded-full hover:bg-white/10 flex items-center justify-center">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
          {messages.map((m, i) => (
            <div key={i} className={`flex items-start gap-2 ${m.from === "user" ? "justify-end" : ""}`}>
              {m.from === "bot" && (
                <div className="h-7 w-7 shrink-0 rounded-full bg-yellow-400 flex items-center justify-center">
                  <Bot className="h-3.5 w-3.5 text-[#0B2A6B]" />
                </div>
              )}
              <div
                className={`px-3.5 py-2.5 rounded-2xl text-sm max-w-[78%] leading-relaxed ${
                  m.from === "bot"
                    ? "bg-white text-slate-800 border border-slate-100 rounded-tl-sm"
                    : "bg-[#0B2A6B] text-white rounded-tr-sm"
                }`}
              >
                {m.text}
              </div>
              {m.from === "user" && (
                <div className="h-7 w-7 shrink-0 rounded-full bg-slate-200 flex items-center justify-center">
                  <User className="h-3.5 w-3.5 text-slate-600" />
                </div>
              )}
            </div>
          ))}

          {/* Course quick-replies */}
          {phase === phases.ASK_COURSE && (
            <div className="flex flex-wrap gap-1.5 pt-1 pl-9">
              {COURSES.map((c) => (
                <button
                  key={c}
                  onClick={() => handleSend(c)}
                  className="px-2.5 py-1 text-[11px] font-semibold rounded-full bg-yellow-100 text-[#0B2A6B] border border-yellow-200 hover:bg-yellow-400 transition"
                >
                  {c}
                </button>
              ))}
            </div>
          )}

          <div ref={endRef} />
        </div>

        {/* Input */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSend();
          }}
          className="border-t border-slate-200 p-3 flex items-center gap-2 bg-white"
        >
          <input
            type={phase === phases.ASK_PHONE ? "tel" : "text"}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={
              phase === phases.ASK_NAME
                ? "Your name"
                : phase === phases.ASK_PHONE
                ? "+91 98xxxxxxxx"
                : phase === phases.DONE
                ? "Chat closed"
                : "Type a message..."
            }
            disabled={phase === phases.DONE}
            className="flex-1 px-3 py-2.5 text-sm rounded-full bg-slate-100 border border-transparent focus:bg-white focus:border-yellow-400 focus:outline-none transition"
          />
          <button
            type="submit"
            disabled={phase === phases.DONE || !draft.trim()}
            className="h-10 w-10 rounded-full bg-yellow-400 hover:bg-yellow-500 text-[#0B2A6B] flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            <Send className="h-4 w-4" />
          </button>
        </form>
      </div>
    </>
  );
}
